import type { CSSProperties } from "react";

export type IconProps = {
  size?: number;
  color?: string;
  strokeWidth?: number;
  style?: CSSProperties;
  className?: string;
  "aria-hidden"?: boolean;
};

/**
 * Lucide-style stroke icons.
 *
 * Design system mandates Lucide aesthetics: 1.5px stroke, sharp corners,
 * no unicode glyph substitutions. We hand-roll a tiny subset rather than
 * pull in lucide-react (~12KB) since we only need a handful.
 *
 * All icons share the same props shape so they can be slotted anywhere
 * an inline SVG is expected.
 */
function svgProps(p: IconProps) {
  return {
    width: p.size ?? 16,
    height: p.size ?? 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: p.color ?? "currentColor",
    strokeWidth: p.strokeWidth ?? 1.5,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    style: p.style,
    className: p.className,
    "aria-hidden": p["aria-hidden"] ?? true,
  };
}

export function IconCheck(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function IconX(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function IconStar(p: IconProps & { filled?: boolean }) {
  const { filled, ...rest } = p;
  return (
    <svg {...svgProps(rest)} fill={filled ? (p.color ?? "currentColor") : "none"}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

export function IconChevronLeft(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

export function IconChevronRight(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export function IconArrowRight(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

export function IconTarget(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

export function IconTimer(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <line x1="10" y1="2" x2="14" y2="2" />
      <line x1="12" y1="14" x2="15" y2="11" />
      <circle cx="12" cy="14" r="8" />
    </svg>
  );
}

export function IconWifiOff(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <line x1="2" y1="2" x2="22" y2="22" />
      <path d="M8.5 16.5a5 5 0 0 1 7 0" />
      <path d="M2 8.82a15 15 0 0 1 4.17-2.65" />
      <path d="M10.66 5c4.01-.36 8.14.9 11.34 3.76" />
      <path d="M16.85 11.25a10 10 0 0 1 2.22 1.68" />
      <path d="M5 13a10 10 0 0 1 5.17-2.69" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}

export function IconHome(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

export function IconPhone(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </svg>
  );
}
